import React, { useEffect, useState } from "react";
import {
  MotifDatePicker,
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
  MotifModalHeader,
} from "@ey-xd/motif-react";
import { CalendarIcon } from "../../assets/Icons/Calendar";
import { CloseIcon } from "../../assets/Icons/CloseIcon";
import { limitComment, MAX_COMMENT_LENGTH } from "./laap.utils";
import "./EditUnavailabilityModal.scss";

const parseDateRange = (dateRange = "") => {
  const [startValue, endValue] = dateRange.split(" - ");
  const startDate = startValue ? new Date(startValue) : null;
  const endDate = endValue ? new Date(endValue) : null;

  return [
    startDate && !Number.isNaN(startDate.getTime()) ? startDate : null,
    endDate && !Number.isNaN(endDate.getTime()) ? endDate : null,
  ];
};

const formatDate = (date) =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const isSameCalendarDay = (startDate, endDate) =>
  startDate instanceof Date &&
  endDate instanceof Date &&
  startDate.getFullYear() === endDate.getFullYear() &&
  startDate.getMonth() === endDate.getMonth() &&
  startDate.getDate() === endDate.getDate();

export const EditUnavailabilityModal = ({
  show,
  record,
  onClose,
  onSave,
  isSaving = false,
  error = "",
}) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [halfDay, setHalfDay] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!show || !record) {
      return;
    }

    const [recordStartDate, recordEndDate] = parseDateRange(record.dates);
    setStartDate(recordStartDate);
    setEndDate(recordEndDate);
    setHalfDay(
      Boolean(record.halfDay) && isSameCalendarDay(recordStartDate, recordEndDate),
    );
    setReason(limitComment(record.reason));
  }, [record, show]);

  const hasInvalidDateRange =
    startDate instanceof Date &&
    endDate instanceof Date &&
    startDate.getTime() > endDate.getTime();
  const canSave =
    startDate instanceof Date &&
    endDate instanceof Date &&
    !hasInvalidDateRange;
  const isSingleDayRange = isSameCalendarDay(startDate, endDate);

  const handleDateChange = (value) => {
    if (!value) {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    const [nextStartDate, nextEndDate] = Array.isArray(value)
      ? value
      : [value, null];
    setStartDate(nextStartDate instanceof Date ? nextStartDate : null);
    setEndDate(nextEndDate instanceof Date ? nextEndDate : null);
    if (!isSameCalendarDay(nextStartDate, nextEndDate)) {
      setHalfDay(false);
    }
  };

  const handleSave = () => {
    if (!record || !canSave) {
      return;
    }

    onSave({
      ...record,
      startDate,
      endDate,
      dates: `${formatDate(startDate)} - ${formatDate(endDate)}`,
      halfDay,
      reason: limitComment(reason).trim(),
    });
  };

  return (
    <MotifModal
      show={show}
      onClose={onClose}
      size="md"
      className="laap-edit-modal"
    >
      <MotifModalHeader>Edit your Unavailability</MotifModalHeader>
      <MotifModalBody>
        <div className="laap-edit-profile">
          <span className="laap-edit-label">Name</span>
          <span className="laap-edit-name">{record?.name || ""}</span>
          <span className="laap-edit-email">{record?.email || ""}</span>
        </div>

        <div className="laap-edit-field">
          <label className="laap-edit-label">
            Date range <span className="laap-required-marker">*</span>
          </label>
          <div
            className={`laap-edit-datepicker${hasInvalidDateRange ? " laap-edit-datepicker--error" : ""}`}
          >
            <MotifDatePicker
              range
              value={[startDate, endDate]}
              format="dd/MM/yyyy"
              locale="en-US"
              calendarPlacement="bottom-start"
              yearsAfter={5}
              yearsBefore={5}
              onChange={handleDateChange}
              dayPlaceholder="dd"
              monthPlaceholder="mm"
              yearPlaceholder="yyyy"
              clearIcon={
                startDate || endDate ? (
                  <CloseIcon width={16} height={16} color="#A4A3B1" />
                ) : null
              }
              calendarIcon={
                <CalendarIcon width={16} height={16} color="#A4A3B1" />
              }
            />
          </div>
          {hasInvalidDateRange && (
            <span className="laap-edit-error" role="alert">
              Start date cannot be before End date
            </span>
          )}
        </div>

        <label className="laap-edit-half-day">
          <input
            type="checkbox"
            checked={halfDay}
            disabled={!isSingleDayRange}
            title={!isSingleDayRange ? "Half Day is available for a single date only" : undefined}
            onChange={(event) => setHalfDay(event.target.checked)}
          />
          <span>Half Day</span>
        </label>

        <label className="laap-edit-field">
          <span className="laap-edit-label">Reason (Optional)</span>
          <input
            type="text"
            value={reason}
            maxLength={MAX_COMMENT_LENGTH}
            onChange={(event) => setReason(limitComment(event.target.value))}
            placeholder="Write Comments Here"
          />
        </label>

        {error && (
          <span className="laap-edit-error" role="alert">
            {error}
          </span>
        )}
      </MotifModalBody>
      <MotifModalFooter>
        <div className="laap-edit-footer-actions">
          <button type="button" className="laap-edit-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="laap-edit-save"
            disabled={!canSave || isSaving}
            onClick={handleSave}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </MotifModalFooter>
    </MotifModal>
  );
};

export default EditUnavailabilityModal;
